import { useId, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { mensajeDeCampo } from '../api';
import type { RotacionKey, Sistema, TenantResumen } from '../api';
import {
  activarSistema,
  autorizarTenant,
  cambiosDeSistema,
  desactivarSistema,
  modificarSistema,
  revocarTenant,
  rotarKey,
} from './api';
import { useRefrescarSistemas } from './consultas';
import { KeyEnClaro } from './KeyEnClaro';
import { ListaBlanca } from './ListaBlanca';
import { mensajeDeSistemas } from './mensajes';
import type { Operacion } from './mensajes';

/**
 * Una fila del ABM de sistemas conectados (KAN-144).
 *
 * Repite el patrón que dejó KAN-143 —**una sola mutación por fila**, con su
 * propio error mostrado donde se apretó el botón— y le agrega las dos cosas que
 * los sistemas tienen y las personas no: la key rotable y la lista blanca.
 *
 * # LA KEY NO SE MUESTRA ACÁ: NO LA TENEMOS
 *
 * La fila muestra el **prefijo** (`apiKeyPrefijo`), que es lo que permite saber
 * de qué key habla un log y cuál hay que rotar, y **cuándo se rotó por última
 * vez**. La key entera no está en `Sistema` y no hay endpoint que la devuelva:
 * lo único que puede hacer aparecer una key acá es rotarla, y eso genera una
 * nueva.
 *
 * # ROTAR SE CONFIRMA DICIENDO LA CONSECUENCIA
 *
 * No un "¿estás seguro?" —a esa pregunta todo el mundo contesta que sí—. La
 * confirmación dice lo que va a pasar: **la aplicación que use la key actual deja
 * de poder emitir en el acto**, hasta que alguien le configure la nueva. Rotar es
 * lo correcto ante una key filtrada o perdida; también es un corte de servicio
 * planificado, y quien aprieta tiene que saber cuál de las dos cosas está
 * haciendo.
 *
 * No se usa `window.confirm`: no deja escribir la consecuencia con formato, no se
 * puede probar sin sustituirlo y no es la misma pantalla.
 */
export function FilaDeSistema({
  sistema,
  tenants,
}: {
  sistema: Sistema;
  /** Los tenants para elegir por nombre. Ver `ListaBlanca`. */
  tenants: readonly TenantResumen[];
}) {
  const idNombre = useId();
  const idPanel = useId();

  const [modo, setModo] = useState<'ver' | 'editar' | 'tenants' | 'rotar'>('ver');
  const [nombre, setNombre] = useState(sistema.nombre);

  const refrescar = useRefrescarSistemas();

  const accion = useMutation({
    mutationFn: (pedido: Pedido) => ejecutar(sistema.id, pedido),
    // `gcTime: 0` NO es una optimización: es lo que hace que la key en claro sea
    // efímera de verdad. Por default TanStack Query conserva el resultado de una
    // mutación **5 minutos** en el `MutationCache` del cliente después de que
    // nadie la observe, y `reset()` solo desengancha al observador: el objeto
    // —con la key adentro— se queda ahí. Con `gcTime: 0` se descarta apenas se
    // cierra el panel. Hay un test que falla si alguien lo saca.
    gcTime: 0,
    onSuccess: () => {
      // El resultado NO se copia a ningún estado propio: la única operación que
      // devuelve algo es la rotación, y su key se lee directo de `accion.data`
      // mientras el panel está abierto. Copiarla a un `useState` sería un
      // segundo lugar del que habría que acordarse de borrarla.
      setModo('ver');
      refrescar();
    },
  });

  /**
   * La key recién rotada, **solo mientras el panel está abierto**.
   *
   * Vive en el estado de la mutación y en ningún otro lado: no se cachea (esto
   * es una mutación, no una query), no se persiste, no se loguea, y se descarta
   * con `accion.reset()` al cerrar el panel.
   */
  const keyNueva: RotacionKey | null = accion.data ?? null;

  const errorDeNombre = mensajeDeCampo(accion.error, 'nombre');

  function guardarEdicion(): void {
    const cambios = cambiosDeSistema(sistema, { nombre });
    if (!cambios) {
      setModo('ver'); // Nada cambió: no se gasta un pedido.
      return;
    }
    accion.mutate({ tipo: 'renombrar', cambios });
  }

  /** Cerrar el panel **borra la key**: `reset()` limpia el estado de la mutación. */
  function cerrarKey(): void {
    accion.reset();
  }

  return (
    <>
      <tr className={sistema.activo ? undefined : 'inactivo'}>
        <td>
          {modo === 'editar' ? (
            <>
              <label htmlFor={idNombre} className="oculto-visualmente">
                Nombre de {sistema.nombre}
              </label>
              <input
                id={idNombre}
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                aria-invalid={errorDeNombre !== null}
                aria-describedby={errorDeNombre ? `${idNombre}-error` : undefined}
              />
              {errorDeNombre ? (
                <span id={`${idNombre}-error`} className="error">
                  {' '}
                  {errorDeNombre}
                </span>
              ) : null}
            </>
          ) : (
            sistema.nombre
          )}
        </td>
        <td>
          {/* El prefijo, no la key. Es lo que identifica a la key en un log sin
              ser el secreto. */}
          <code>{sistema.apiKeyPrefijo}…</code>
        </td>
        <td>{textoDeRotacion(sistema.keyRotadaEn)}</td>
        <td>
          {sistema.tenantsAutorizados.length === 0 ? (
            <span className="nota">Ninguno</span>
          ) : (
            sistema.tenantsAutorizados.length
          )}
        </td>
        <td>{sistema.activo ? 'Activo' : 'Inactivo'}</td>
        <td>
          {modo === 'ver' ? (
            <Acciones
              sistema={sistema}
              ocupada={accion.isPending}
              alEditar={() => {
                setNombre(sistema.nombre);
                accion.reset();
                setModo('editar');
              }}
              alAbrirTenants={() => {
                accion.reset();
                setModo('tenants');
              }}
              alPedirRotacion={() => {
                accion.reset();
                setModo('rotar');
              }}
              alDesactivar={() => accion.mutate({ tipo: 'desactivar' })}
              alActivar={() => accion.mutate({ tipo: 'activar' })}
            />
          ) : null}

          {modo === 'editar' ? (
            <>
              <button
                type="button"
                aria-label={`Guardar los cambios de ${sistema.nombre}`}
                disabled={accion.isPending}
                onClick={guardarEdicion}
              >
                Guardar
              </button>{' '}
              <button
                type="button"
                aria-label={`Cancelar la edición de ${sistema.nombre}`}
                onClick={() => setModo('ver')}
              >
                Cancelar
              </button>
            </>
          ) : null}

          {modo === 'tenants' || modo === 'rotar' ? (
            <button
              type="button"
              aria-label={`Cerrar el panel de ${sistema.nombre}`}
              aria-expanded="true"
              aria-controls={idPanel}
              onClick={() => setModo('ver')}
            >
              Cerrar
            </button>
          ) : null}
        </td>
      </tr>

      {modo === 'tenants' ? (
        <tr>
          <td colSpan={6} id={idPanel}>
            <ListaBlanca
              sistema={sistema}
              tenants={tenants}
              ocupada={accion.isPending}
              alAutorizar={(tenantId) => accion.mutate({ tipo: 'autorizar', tenantId })}
              alRevocar={(tenantId) => accion.mutate({ tipo: 'revocar', tenantId })}
            />
          </td>
        </tr>
      ) : null}

      {modo === 'rotar' ? (
        <tr>
          <td colSpan={6} id={idPanel}>
            <ConfirmacionDeRotacion
              sistema={sistema}
              ocupada={accion.isPending}
              alConfirmar={() => accion.mutate({ tipo: 'rotar' })}
              alCancelar={() => setModo('ver')}
            />
          </td>
        </tr>
      ) : null}

      {accion.isError ? (
        <tr className="fila-error">
          <td colSpan={6}>
            <span role="alert" className="error">
              {mensajeDeSistemas(accion.error, operacionDe(accion.variables))}
            </span>
          </td>
        </tr>
      ) : null}

      {keyNueva ? (
        <KeyEnClaro
          apiKey={keyNueva.apiKey}
          nombre={sistema.nombre}
          motivo="rotacion"
          alCerrar={cerrarKey}
        />
      ) : null}
    </>
  );
}

/**
 * La confirmación de la rotación.
 *
 * Dice **qué va a pasar**, no "¿estás seguro?". Y dice también para qué sirve
 * rotar, porque quien llega acá suele llegar por una key filtrada o perdida y
 * necesita saber que está en el lugar correcto.
 */
function ConfirmacionDeRotacion({
  sistema,
  ocupada,
  alConfirmar,
  alCancelar,
}: {
  sistema: Sistema;
  ocupada: boolean;
  alConfirmar: () => void;
  alCancelar: () => void;
}) {
  return (
    <div>
      <h3>Rotar la API key de {sistema.nombre}</h3>
      <p className="error">
        La key actual (<code>{sistema.apiKeyPrefijo}…</code>) deja de servir en el acto.{' '}
        <strong>
          {sistema.nombre} no va a poder emitir ningún comprobante hasta que alguien le configure la
          key nueva.
        </strong>
      </p>
      <p className="nota">
        Es lo que corresponde hacer si la key se filtró o se perdió: no hay período de gracia ni
        conviven dos keys, rotar es revocar. La key nueva se muestra una sola vez, ahora.
      </p>
      <p>
        <button
          type="button"
          aria-label={`Rotar la key de ${sistema.nombre}`}
          disabled={ocupada}
          onClick={alConfirmar}
        >
          {ocupada ? 'Rotando…' : 'Rotar la key igual'}
        </button>{' '}
        <button
          type="button"
          aria-label={`Cancelar la rotación de ${sistema.nombre}`}
          onClick={alCancelar}
        >
          Cancelar
        </button>
      </p>
    </div>
  );
}

/**
 * Los botones de la fila en reposo.
 *
 * **La baja NUNCA se llama "eliminar".** Es lógica: la fila queda para poder
 * atribuir un comprobante al sistema que lo pidió, y los inactivos siguen
 * listados para que se los pueda reactivar.
 */
function Acciones({
  sistema,
  ocupada,
  alEditar,
  alAbrirTenants,
  alPedirRotacion,
  alDesactivar,
  alActivar,
}: {
  sistema: Sistema;
  ocupada: boolean;
  alEditar: () => void;
  alAbrirTenants: () => void;
  alPedirRotacion: () => void;
  alDesactivar: () => void;
  alActivar: () => void;
}) {
  const quien = sistema.nombre;
  return (
    <>
      <button type="button" aria-label={`Editar ${quien}`} onClick={alEditar}>
        Editar
      </button>{' '}
      <button type="button" aria-label={`Ver los tenants de ${quien}`} onClick={alAbrirTenants}>
        Tenants
      </button>{' '}
      <button type="button" aria-label={`Rotar la API key de ${quien}`} onClick={alPedirRotacion}>
        Rotar key
      </button>{' '}
      {sistema.activo ? (
        <button
          type="button"
          aria-label={`Desactivar ${quien}`}
          disabled={ocupada}
          onClick={alDesactivar}
        >
          Desactivar
        </button>
      ) : (
        <>
          <button
            type="button"
            aria-label={`Reactivar ${quien}`}
            disabled={ocupada}
            onClick={alActivar}
          >
            Reactivar
          </button>
          {/* Reactivar NO genera una key nueva: vuelve a servir la de antes. Si
              el sistema se dio de baja porque su key se filtró, reactivarlo sin
              rotar la deja viva otra vez. El contrato no lo puede impedir; la
              pantalla al menos lo dice. */}
          <span className="nota"> Vuelve a servir la key anterior; si se filtró, rotala.</span>
        </>
      )}
    </>
  );
}

/** Cuándo se rotó por última vez, o que nunca se rotó. */
function textoDeRotacion(cuando: string | null | undefined): string {
  if (!cuando) {
    return 'Nunca';
  }
  const fecha = new Date(cuando);
  return Number.isNaN(fecha.getTime())
    ? cuando
    : fecha.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Lo que se le puede pedir al backend sobre una fila. */
type Pedido =
  | { tipo: 'renombrar'; cambios: { nombre?: string } }
  | { tipo: 'desactivar' }
  | { tipo: 'activar' }
  | { tipo: 'rotar' }
  | { tipo: 'autorizar'; tenantId: number }
  | { tipo: 'revocar'; tenantId: number };

/**
 * Ejecuta el pedido y devuelve **la key nueva, o `null`**.
 *
 * El tipo de retorno es así a propósito: de las seis operaciones, **una sola
 * produce un secreto**, y esta firma lo dice. Devolver `Sistema | RotacionKey`
 * obligaría a una aserción de tipo en `onSuccess` para sacar la key —y una
 * aserción de tipo es exactamente lo que no queremos en el camino por donde pasa
 * una credencial en claro—. Las respuestas que no son la key se descartan: la
 * lista se re-lee.
 */
async function ejecutar(id: number, pedido: Pedido): Promise<RotacionKey | null> {
  switch (pedido.tipo) {
    case 'rotar':
      return rotarKey(id);
    case 'renombrar':
      await modificarSistema(id, pedido.cambios);
      return null;
    case 'desactivar':
      await desactivarSistema(id);
      return null;
    case 'activar':
      await activarSistema(id);
      return null;
    case 'autorizar':
      await autorizarTenant(id, pedido.tenantId);
      return null;
    case 'revocar':
      await revocarTenant(id, pedido.tenantId);
      return null;
  }
}

/**
 * Qué operación falló. Sale de lo que se pidió, **no de mirar la respuesta**: es
 * lo que permite explicar un `500` autorizando un tenant sin leer el `detalle`.
 */
function operacionDe(pedido: Pedido | undefined): Operacion {
  return pedido?.tipo ?? 'renombrar';
}
